import { ErrorHandler, Injectable, Injector, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AppStore } from '../appstate/app.store';
import { envConfig } from '../config/environment';

/**
 * Centralized logging service for the application.
 * This service provides methods for logging at different levels (debug, info, warn, error).
 * It automatically respects the debug logging flag from the AppStore.
 * 
 * Usage:
 * this.logger.debug('MyComponent', 'Items loaded', { count: 3 });
 * this.logger.error('MyComponent', 'Failed to load data', error);
 */
@Injectable({
    providedIn: 'root'
})
export class LoggerService implements ErrorHandler {
    private readonly injector = inject(Injector);
    private readonly LOG_PREFIX = '[DRX-TDP]';
    private readonly generalLoggingEnabled = !envConfig.isProd;

    handleError(error: unknown): void {
        this.error('Application', 'Unhandled application error', error);
    }

    /**
     * Logs a debug message (only shown when debug logging is enabled)
     */
    debug(component: string, message: string, data?: unknown): void {
        if (this.injector.get(AppStore).isDebugLoggingEnabled()) {
            this.logToConsole('DEBUG', component, message, data);
        }
    }

    /**
     * Logs an info message (only shown when dev environment)
     */
    info(component: string, message: string, data?: unknown): void {
        if (this.generalLoggingEnabled) {
            this.logToConsole('INFO', component, message, data);
        }
    }

    /**
     * Logs a warning message (only shown when dev environment)
     */
    warn(component: string, message: string, data?: unknown): void {
        if (this.generalLoggingEnabled) {
            this.logToConsole('WARN', component, message, data);
        }
    }

    /**
     * Logs an error message (only shown when dev environment)
     */
    error(component: string, message: string, error?: unknown): void {
        if (this.generalLoggingEnabled) {
            const details = error instanceof Error || error instanceof HttpErrorResponse
                ? { name: error.name, ...('status' in error ? { status: error.status } : {}) }
                : error === undefined ? undefined : { type: typeof error };
            this.logToConsole('ERROR', component, message, details);
        }
    }

    /**
     * Private method to format and log messages to console (used internally by the service)
     */
    private logToConsole(level: 'DEBUG' | 'INFO' | 'WARN' | 'ERROR', component: string, message: string, data?: unknown): void {
        const timestamp = new Date().toISOString();
        const formattedMessage = `${this.LOG_PREFIX} [${timestamp}] [${level}] [${component}] ${message}`;

        switch (level) {
        case 'DEBUG':
            console.debug(formattedMessage, data ?? '');
            break;
        case 'INFO':
            console.log(formattedMessage, data ?? '');
            break;
        case 'WARN':
            console.warn(formattedMessage, data ?? '');
            break;
        case 'ERROR':
            console.error(formattedMessage, data ?? '');
            break;
        }
    }
}
